import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as RemoveRowIcon, Add as AddRowIcon } from '@mui/icons-material';
import { Control, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { TripLeg } from '../../../data/models/reservation.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import * as reservationSlice from '../../../data/reservation.slice';
import * as uiSlice from '../../../data/ui.slice';
import SelectMenu from '../../forms/SelectMenu';
import TimeInput from '../../forms/TimeInput';
import DateInput from '../../forms/DateInput';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import tripLegUtils from '../../../services/triplegUtils';

interface EditTripLegTimesDialogProps {
  tripLeg: TripLeg;
  open: boolean;
  close: () => void;
}

const PREFIX = 'EditTripLegTimesDialog';

const classes = {
  subtitle: `${PREFIX}-subtitle`,
  content: `${PREFIX}-content`,
  status: `${PREFIX}-status`,
};

type EditableStatus =
  | TripLegStatus.Onsite
  | TripLegStatus.NoLoad
  | TripLegStatus.Onboard
  | TripLegStatus.Dropoff;

const validNextStatuses: Record<EditableStatus, EditableStatus[]> = {
  [TripLegStatus.Onsite]: [
    TripLegStatus.NoLoad,
    TripLegStatus.Onboard,
    TripLegStatus.Dropoff,
  ],
  [TripLegStatus.NoLoad]: [],
  [TripLegStatus.Onboard]: [TripLegStatus.Dropoff],
  [TripLegStatus.Dropoff]: [],
};

type StatusSelection = { bc: EditableStatus; name: string };

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.subtitle}`]: {
    display: 'flex',
    fontWeight: 800,
    fontSize: 18,
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'row',
    minWidth: 500,
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.status}`]: {
    width: '200px',
  },
}));

export default function EditTripLegTimesDialog({
  tripLeg,
  open,
  close,
}: EditTripLegTimesDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const tripTimeZone = tripLeg.pickUpAddressTimeZoneIANA || undefined;

  const editableStatuses: StatusSelection[] = [
    { bc: TripLegStatus.Onsite, name: t('tripLegStatus.onsite') },
    { bc: TripLegStatus.NoLoad, name: t('tripLegStatus.noload') },
    { bc: TripLegStatus.Onboard, name: t('tripLegStatus.onboard') },
    { bc: TripLegStatus.Dropoff, name: t('tripLegStatus.dropoff') },
  ];

  const initialStatuses = editableStatuses.filter(
    (status) => status.bc !== TripLegStatus.Onboard,
  );

  const [validStatuses, setValidStatuses] = useState([initialStatuses]);

  const loadedReopen = useAppSelector(
    reservationSlice.selectLoadedReopenEditTripLegTime,
  );
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);

  React.useEffect(() => {
    if (saveSuccessMessage && loadedReopen) {
      handleClose();
      dispatch(reservationSlice.setLoadedReopenEditTripLegTime(false));
    }
  }, [saveSuccessMessage, loadedReopen]);

  const { control, formState, getValues, handleSubmit, watch } = useForm({
    defaultValues: {
      tripLegReferenceID: tripLeg.tripLegReferenceID,
      passengerUUID: tripLeg.passenger?.passengerUUID,
      Events: [
        {
          status: '',
          date: tripLegUtils.getDefaultTripEventDate(tripTimeZone),
        },
      ],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'Events' as any,
  });

  const isFormValid: boolean = useMemo(() => {
    const vals = getValues();
    const events = vals.Events;
    let isValid = events.length > 0 && formState.isValid;
    events.forEach((e) => {
      isValid = isValid && !!e.status && !!e.date?.isValid;
    });
    return isValid;
  }, [formState, watch()]);

  const toWatch = editableStatuses
    .map((_, index) => index)
    .map((index) => `Events.${index}.status`) as any[];
  useEffect(() => {
    // Calculate the valid statuses based on the current selection
    let updatedStatuses = getValues().Events.map((event) => {
      const validFromStatuses =
        validNextStatuses[event.status as EditableStatus] ?? [];
      const mappedStatuses = validFromStatuses
        .map((status) => editableStatuses.find((et) => et.bc === status))
        .filter((status) => !!status) as StatusSelection[];
      return mappedStatuses;
    });
    updatedStatuses = [initialStatuses, ...updatedStatuses];
    // Remove any rows that are no longer valid based on the current selection
    getValues().Events.forEach((event, index) => {
      if (event.status) {
        const isStatusValid = updatedStatuses[index].find(
          (status) => status.bc === event.status,
        );
        if (!isStatusValid) {
          removeRow(index);
        }
      }
    });
    setValidStatuses(updatedStatuses);
  }, watch(toWatch));

  const handleClose = () => {
    close();
  };

  const sendUpdate = (
    index: number,
    evt: { status: string; date: DateTime },
    passengerUUID?: string,
    tripLegReferenceID?: string,
  ) => {
    setTimeout(() => {
      const date = tripLegUtils.toTripEventDateTimeUTCISO(
        evt.date,
        tripTimeZone,
      );
      if (passengerUUID && tripLegReferenceID && evt.status && date) {
        dispatch(
          reservationSlice.updateTripLegStatusAsync(
            passengerUUID,
            tripLegReferenceID,
            evt.status,
            date,
          ),
        );
      }
    }, 5000 * index);
  };

  const submit = (data: any) => {
    const events = data.Events;
    const passengerUUID = tripLeg.passengerUUID;
    const tripLegReferenceID = tripLeg.tripLegReferenceID;
    events.forEach((evt: { status: string; date: DateTime }, idx: number) => {
      sendUpdate(idx, evt, passengerUUID, tripLegReferenceID);
    });
    handleClose();
  };

  const removeRow = (index: number) => {
    remove(index);
  };

  const addRow = () => {
    append({
      status: '',
      date: tripLegUtils.getDefaultTripEventDate(tripTimeZone),
    });
  };

  return (
    <>
      <RootDialog open={open} onClose={handleClose} maxWidth={'xl'}>
        <DialogTitle>{t('triplegInventory.editEventTimesTitle')}</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Typography className={classes.subtitle}>
              {`${t('triplegInventory.tripId')}: ${tripLeg.tripLegReferenceID}`}
            </Typography>
            {fields.map((item, idx) => {
              return (
                <Box className={classes.content} key={item.id}>
                  <SelectMenu
                    label={t('triplegInventory.status')}
                    name={`Events[${idx}].status`}
                    values={validStatuses[idx]}
                    valueAttr="bc"
                    labelAttr="name"
                    control={control}
                    required={true}
                    className={classes.status}
                  />
                  <DateInput
                    name={`Events[${idx}].date`}
                    label={t('triplegInventory.date')}
                    control={control as Control<any>}
                    required={true}
                    timezone={tripTimeZone}
                  />
                  <TimeInput
                    name={`Events[${idx}].date`}
                    label={t('triplegInventory.time')}
                    control={control as Control<any>}
                    required={true}
                    timezone={tripTimeZone}
                  />
                  <IconButton
                    data-testid={`remove-row-${idx}`}
                    onClick={() => {
                      removeRow(idx);
                    }}
                    size="large">
                    <RemoveRowIcon />
                  </IconButton>
                </Box>
              );
            })}
            {validStatuses.length > 0 &&
              validStatuses[validStatuses.length - 1].length > 0 && (
                <Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      addRow();
                    }}>
                    {t('triplegInventory.addEventTime')}
                  </Button>
                </Box>
              )}
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isFormValid}
            onClick={handleSubmit(submit)}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </RootDialog>
    </>
  );
}
